/**
 * Entitlement Service
 * Fetches and caches user entitlements (tier features, usage, credits)
 *
 * The service now supports the new tierInfo object in responses which
 * provides tier metadata (displayName, badgeLabel, isPaid) from the database.
 */

import { authService } from '../auth/auth-service.js';
import { SUPABASE_URL, isConfigured } from '../config/config.js';

/**
 * Deep freeze an object to prevent modification
 * @param {Object} obj - Object to freeze
 * @returns {Object} Frozen object
 */
function deepFreeze(obj) {
  if (obj === null || typeof obj !== 'object') return obj;
  Object.freeze(obj);
  Object.getOwnPropertyNames(obj).forEach(prop => {
    if (obj[prop] !== null && typeof obj[prop] === 'object' && !Object.isFrozen(obj[prop])) {
      deepFreeze(obj[prop]);
    }
  });
  return obj;
}

class EntitlementService {
  constructor() {
    this.cache = null;
    this.cacheExpiry = null;
    this.cacheDuration = 5 * 60 * 1000; // 5 minutes
    this.listeners = new Set();
    // SECURITY: Signature to verify freeMode came from server
    this.freeModeSignature = null;
    // Deduplication: reuse in-flight fetch for concurrent callers
    this.pendingFetch = null;
  }

  /**
   * Get current entitlements (cached)
   */
  async getEntitlements(forceRefresh = false) {
    // Return cached if still valid
    if (!forceRefresh && this.cache && this.cacheExpiry > Date.now()) {
      return this.cache;
    }

    // Deduplicate concurrent calls: if a fetch is already in flight, reuse it
    if (!forceRefresh && this.pendingFetch) {
      return this.pendingFetch;
    }

    // Determine if user is authenticated (and not anonymous)
    const isReallyAuthenticated = authService.isAuthenticated() && !authService.isAnonymous();

    // If not configured or not authenticated, return fallback entitlements
    if (!isConfigured() || !authService.isAuthenticated()) {
      // SECURITY: Freeze cache to prevent tampering via console
      this.cache = deepFreeze(this.getFallbackEntitlements(false));
      this.cacheExpiry = Date.now() + this.cacheDuration;
      return this.cache;
    }

    // Wrap the fetch in a promise for deduplication
    this.pendingFetch = (async () => {
      try {
        // Fetch from server
        const entitlements = await this.fetchEntitlementsFromServer();
        // SECURITY: Store freeMode signature if present (for verification)
        if (entitlements.freeMode && entitlements._freeModeNonce) {
          this.freeModeSignature = entitlements._freeModeNonce;
        } else {
          this.freeModeSignature = null;
        }
        // SECURITY: Freeze cache to prevent tampering via console
        this.cache = deepFreeze(entitlements);
        this.cacheExpiry = Date.now() + this.cacheDuration;
        this.notifyListeners();
        return this.cache;
      } catch (error) {
        console.error('[EntitlementService] Error fetching entitlements:', error);
        // SECURITY: Clear freeMode signature on error (no trusted source)
        this.freeModeSignature = null;
        // Fall back to minimal permissions based on auth state
        // SECURITY: Freeze cache to prevent tampering via console
        this.cache = deepFreeze(this.getFallbackEntitlements(isReallyAuthenticated));
        this.cacheExpiry = Date.now() + 60 * 1000; // Short cache on error
        return this.cache;
      } finally {
        this.pendingFetch = null;
      }
    })();

    return this.pendingFetch;
  }

  /**
   * Fetch entitlements from server
   */
  async fetchEntitlementsFromServer() {
    const accessToken = authService.getAccessToken();
    if (!accessToken) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${SUPABASE_URL}/functions/v1/get-entitlements`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Server error: ${response.status}`);
    }

    return await response.json();
  }

  /**
   * Validate export with server
   * SECURITY: Server is the sole authority for premium feature access.
   * Network errors result in rejection, not local fallback.
   */
  async validateExport(params) {
    // SECURITY: If not configured, reject (no server = no premium validation)
    if (!isConfigured()) {
      return {
        allowed: false,
        reason: 'server_not_configured',
        message: 'Server configuration required for exports.'
      };
    }

    // SECURITY: If not authenticated, require sign-in for exports
    if (!authService.isAuthenticated()) {
      return {
        allowed: false,
        reason: 'authentication_required',
        message: 'Please sign in to export videos.'
      };
    }

    try {
      const accessToken = authService.getAccessToken();

      const response = await fetch(`${SUPABASE_URL}/functions/v1/validate-export`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(params)
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        // 400 with details = input validation failure — return as structured result
        if (response.status === 400 && errorBody.details) {
          return {
            allowed: false,
            reason: 'validation_failed',
            message: errorBody.details.map(d => d.message).join('. '),
            details: errorBody.details
          };
        }
        throw new Error(errorBody.message || errorBody.error || `Server error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('[EntitlementService] Export validation error:', error);
      // SECURITY: Never grant premium features when server unavailable
      return {
        allowed: false,
        reason: 'server_unavailable',
        message: 'Export validation requires server connection. Please check your internet and try again.'
      };
    }
  }

  /**
   * Validate export locally (fallback)
   */
  validateExportLocally(params) {
    // Use cached entitlements, or fall back to minimal permissions
    const isReallyAuthenticated = authService.isAuthenticated() && !authService.isAnonymous();
    const entitlements = this.cache || this.getFallbackEntitlements(isReallyAuthenticated);
    const { visualizerType, resolution, durationSeconds } = params;

    // Check visualizer access
    // Allow if: '*' in list, OR visualizer in list, OR user visualizer + canCreateCustomVisualizers
    const allowedVis = entitlements.features.allowedVisualizers;
    const isUserVisualizer = visualizerType.startsWith('user-');
    const isAllowed = allowedVis.includes('*') ||
                      allowedVis.includes(visualizerType) ||
                      (isUserVisualizer && entitlements.features.canCreateCustomVisualizers);

    if (!isAllowed) {
      return {
        allowed: false,
        reason: 'visualizer_not_allowed',
        message: `${visualizerType} is not available on your plan`
      };
    }

    // Check resolution
    const [reqWidth] = resolution.split('x').map(Number);
    const [maxWidth] = entitlements.features.maxResolution.split('x').map(Number);
    if (reqWidth > maxWidth) {
      return {
        allowed: false,
        reason: 'resolution_exceeded',
        message: `Maximum resolution for your plan is ${entitlements.features.maxResolution}`
      };
    }

    // Check per-export duration limit
    const maxDuration = entitlements.features.maxExportDurationSeconds;
    if (maxDuration != null && maxDuration !== -1 && durationSeconds > maxDuration) {
      const limitHours = Math.floor(maxDuration / 3600);
      const limitMins = Math.round((maxDuration % 3600) / 60);
      const limitStr = limitHours > 0 ? `${limitHours}h ${limitMins}m` : `${limitMins}m`;
      return {
        allowed: false,
        reason: 'duration_exceeded',
        message: `Your plan allows exports up to ${limitStr}. Please exclude some tracks or upgrade your plan.`,
        maxDurationSeconds: maxDuration
      };
    }

    // Check export minutes remaining
    const durationMinutes = durationSeconds / 60;
    if (entitlements.features.monthlyExportMinutes !== -1) {
      const remaining = entitlements.usage?.monthlyMinutesRemaining || 0;
      if (durationMinutes > remaining) {
        return {
          allowed: false,
          reason: 'monthly_limit_exceeded',
          message: `Export would exceed monthly limit. ${remaining.toFixed(1)} minutes remaining.`,
          creditsRequired: Math.ceil((durationMinutes - remaining) * 5),
          creditsAvailable: entitlements.credits?.balance || 0
        };
      }
    }

    return {
      allowed: true,
      exportToken: 'local-validation', // No actual token in local mode
      creditsToConsume: 0,
      subscriptionExport: true
    };
  }

  /**
   * Get fallback entitlements when server is unavailable or user is not authenticated.
   * Uses minimal permissions to ensure security - server is the sole authority for tier access.
   *
   * @param {boolean} isAuthenticated - Whether user is authenticated (non-anonymous)
   * @returns {Object} Minimal entitlements object
   */
  getFallbackEntitlements(isAuthenticated = false) {
    // Minimal hardcoded values - only free features allowed
    const monthlyMinutes = isAuthenticated ? 10 : 2;

    return {
      tier: isAuthenticated ? 'solo' : 'anonymous',
      tierInfo: {
        displayName: isAuthenticated ? 'Solo' : 'Guest',
        badgeLabel: isAuthenticated ? 'SOLO' : 'GUEST',
        isPaid: false
      },
      status: 'active',
      features: {
        allowedVisualizers: ['bars', 'bubbles'],  // Only free visualizers
        maxPlaylistSongs: isAuthenticated ? 3 : 1,
        monthlyExportMinutes: monthlyMinutes,
        maxExportDurationSeconds: isAuthenticated ? 7200 : 600,
        maxResolution: '1280x720',  // Lowest resolution
        exportFps: [30],  // Only 30fps
        canCreateCustomVisualizers: false,
        watermarkFree: false,
        prioritySupport: false,
        maxPresets: isAuthenticated ? 10 : 0  // Anonymous users get localStorage only
      },
      usage: {
        monthlyExportsUsed: 0,
        monthlyMinutesUsed: 0,
        monthlyMinutesRemaining: monthlyMinutes
      },
      credits: {
        balance: 0,
        pricePerMinute: 5
      },
      subscription: {
        currentPeriodEnd: null,
        cancelAtPeriodEnd: false
      }
    };
  }

  /**
   * Get anonymous entitlements (legacy alias)
   * @deprecated Use getFallbackEntitlements(false) instead
   */
  getAnonymousEntitlements() {
    return this.getFallbackEntitlements(false);
  }

  /**
   * Get solo tier entitlements (legacy alias)
   * @deprecated Use getFallbackEntitlements(true) instead
   */
  getSoloEntitlements() {
    return this.getFallbackEntitlements(true);
  }

  /**
   * Get free tier entitlements (legacy alias)
   * @deprecated Use getFallbackEntitlements(true) instead
   */
  getFreeEntitlements() {
    return this.getFallbackEntitlements(true);
  }

  /**
   * Get current tier ID
   */
  async getTier() {
    const entitlements = await this.getEntitlements();
    return entitlements.tier;
  }

  /**
   * Get tier info object (displayName, badgeLabel, isPaid)
   */
  async getTierInfo() {
    const entitlements = await this.getEntitlements();
    return entitlements.tierInfo || {
      displayName: entitlements.tier,
      badgeLabel: entitlements.tier.toUpperCase(),
      isPaid: false
    };
  }

  /**
   * Check if current tier is a paid tier
   */
  async isPaidTier() {
    const tierInfo = await this.getTierInfo();
    return tierInfo.isPaid;
  }

  /**
   * Get badge label for current tier
   */
  async getBadgeLabel() {
    const tierInfo = await this.getTierInfo();
    return tierInfo.badgeLabel;
  }

  /**
   * Get display name for current tier
   */
  async getDisplayName() {
    const tierInfo = await this.getTierInfo();
    return tierInfo.displayName;
  }

  /**
   * Get allowed visualizers for current tier
   */
  async getAllowedVisualizers() {
    const entitlements = await this.getEntitlements();
    return entitlements.features.allowedVisualizers;
  }

  /**
   * Get max playlist songs for current tier
   */
  async getMaxPlaylistSongs() {
    const entitlements = await this.getEntitlements();
    return entitlements.features.maxPlaylistSongs;
  }

  /**
   * Get max presets for current tier
   * Returns -1 for unlimited, 0 for anonymous (no cloud sync)
   */
  async getMaxPresets() {
    const entitlements = await this.getEntitlements();
    return entitlements.features?.maxPresets ?? 10;
  }

  /**
   * Get max resolution for current tier
   */
  async getMaxResolution() {
    const entitlements = await this.getEntitlements();
    return entitlements.features.maxResolution;
  }

  /**
   * Get allowed FPS options for current tier
   */
  async getAllowedFps() {
    const entitlements = await this.getEntitlements();
    return entitlements.features.exportFps;
  }

  /**
   * Check if user can create custom visualizers
   */
  async canCreateCustomVisualizers() {
    const entitlements = await this.getEntitlements();
    return entitlements.features.canCreateCustomVisualizers || false;
  }

  /**
   * Check if free mode is enabled (all features unlocked globally)
   * SECURITY: Only returns true if freeMode was legitimately set by server
   */
  async isFreeMode() {
    const entitlements = await this.getEntitlements();
    // Only trust freeMode if it came from server with valid signature
    return entitlements.freeMode === true && this.isFreeModeVerified();
  }

  /**
   * Verify freeMode is legitimately enabled (from server, not cache manipulation)
   * SECURITY: Prevents freeMode bypass via console cache tampering
   * @returns {boolean} True only if freeMode was set by server response with matching nonce
   */
  isFreeModeVerified() {
    if (!this.cache?.freeMode) return false;
    // freeMode must have been received from server with matching nonce
    return this.freeModeSignature !== null &&
           this.cache._freeModeNonce === this.freeModeSignature;
  }

  /**
   * Check if exports are watermark-free
   */
  async hasWatermarkFreeExports() {
    const entitlements = await this.getEntitlements();
    return entitlements.features.watermarkFree || false;
  }

  /**
   * Check if user has priority support
   */
  async hasPrioritySupport() {
    const entitlements = await this.getEntitlements();
    return entitlements.features.prioritySupport || false;
  }

  /**
   * Get credit balance
   */
  async getCreditBalance() {
    const entitlements = await this.getEntitlements();
    return entitlements.credits?.balance || 0;
  }

  /**
   * Get remaining export minutes this month
   */
  async getRemainingExportMinutes() {
    const entitlements = await this.getEntitlements();
    if (entitlements.features.monthlyExportMinutes === -1) {
      return Infinity;
    }
    return entitlements.usage?.monthlyMinutesRemaining || 0;
  }

  /**
   * Complete an export and consume credits if needed
   */
  async completeExport(params) {
    // If not configured or not authenticated, just return success
    if (!isConfigured() || !authService.isAuthenticated()) {
      return { success: true };
    }

    try {
      const accessToken = authService.getAccessToken();

      const response = await fetch(`${SUPABASE_URL}/functions/v1/complete-export`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(params)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || `Server error: ${response.status}`);
      }

      const result = await response.json();

      // Refresh entitlements to update credits/usage
      this.clearCache();
      await this.getEntitlements(true);

      return result;
    } catch (error) {
      console.error('[EntitlementService] Complete export error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Clear cache (call after subscription changes)
   */
  clearCache() {
    this.cache = null;
    this.cacheExpiry = null;
    this.pendingFetch = null;
  }

  /**
   * Subscribe to entitlement changes
   */
  onEntitlementsChange(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Notify listeners of entitlement changes
   */
  notifyListeners() {
    this.listeners.forEach(callback => {
      try {
        callback(this.cache);
      } catch (error) {
        console.error('[EntitlementService] Listener error:', error);
      }
    });
  }
}

// Export singleton instance
export const entitlementService = new EntitlementService();
